import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  UpliftNotification,
  UpliftNotificationItem,
} from "../types/notification";
import { APPLICATION_TYPE } from "../constants/application-types";
import { ENVIRONMENT_TOKEN } from "../types/environment.token";
import { MongoClientService } from "./mongo-client.service";

@Injectable({ providedIn: "root" })
export class NotificationService {
  private _env = inject(ENVIRONMENT_TOKEN);
  private _mongo = inject(MongoClientService);
  private _http = inject(HttpClient);

  fetchValidNotifications(
    userId: string,
    appType: APPLICATION_TYPE,
  ): Observable<UpliftNotificationItem[]> {
    const now = new Date().toISOString();
    return this._mongo.find<UpliftNotificationItem>(
      this._env.notificationDB,
      this._env.notificationItemCollection,
      {
        recipient_user_id: userId,
        active_date: { $lte: now },
        cancelled_date: { $exists: false },
        $and: [
          {
            $or: [
              { application_list: { $size: 0 } },
              { application_list: appType },
            ],
          },
          {
            $or: [
              { expiry_date: "" },
              { expiry_date: { $gt: now } },
            ],
          },
        ],
      }
    ).pipe(
      map((notifications) =>
        notifications.sort(
          (a, b) =>
            new Date(b.active_date).getTime() -
            new Date(a.active_date).getTime(),
        ),
      ),
    );
  }

  markAsViewed(notificationIds: object[]): Observable<any> {
    const now = new Date().toISOString();
    return this._mongo.updateMany(
      this._env.notificationDB,
      this._env.notificationItemCollection,
      { _id: { $in: notificationIds } },
      { $set: { is_viewed: true, viewed_date: now } }
    );
  }

  createNotification(notification: Partial<UpliftNotification>): Observable<any> {
    const endpoint = `${this._env.apiUrl}/api/functions/${this._env.createNotificationFunction}`;
    const { _id, created_by_user_id, created_date, ...fields } = notification as any;
    return this._http.post(endpoint, fields);
  }

  updateNotification(notification: UpliftNotification): Observable<any> {
    const endpoint = `${this._env.apiUrl}/api/functions/${this._env.updateNotificationFunction}`;
    const { _id, created_by_user_id, created_date, last_updated_date, last_updated_user_id, ...fields } = notification as any;
    return this._http.post(endpoint, {
      notificationId: typeof _id === 'object' && _id.toHexString ? _id.toHexString() : String(_id),
      ...fields,
    });
  }

  fetchNotificationsByCreator(
    creatorUserId: string,
  ): Observable<UpliftNotification[]> {
    return this._mongo.find<UpliftNotification>(
      this._env.notificationDB,
      this._env.notificationCollection,
      { created_by_user_id: creatorUserId }
    ).pipe(
      map((notifications) =>
        notifications.sort(
          (a, b) =>
            new Date(b.created_date).getTime() -
            new Date(a.created_date).getTime(),
        ),
      ),
    );
  }

  fetchAllNotifications(
    limit: number = 1000,
    offset: number = 0,
  ): Observable<UpliftNotification[]> {
    return this._mongo.find<UpliftNotification>(
      this._env.notificationDB,
      this._env.notificationCollection,
      {}
    ).pipe(
      map((notifications) => {
        const sorted = notifications.sort(
          (a, b) =>
            new Date(b.created_date).getTime() -
            new Date(a.created_date).getTime(),
        );
        return sorted.slice(offset, offset + limit);
      }),
    );
  }

  fetchNotificationItemsByNotificationId(
    notificationId: string,
  ): Observable<UpliftNotificationItem[]> {
    return this._mongo.find<UpliftNotificationItem>(
      this._env.notificationDB,
      this._env.notificationItemCollection,
      { notification_id: notificationId }
    );
  }
}
